import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Modal,
  Button,
  Form,
  Table,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import { jwtDecode } from "jwt-decode";
import * as Yup from "yup";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FiMinusCircle } from "react-icons/fi";
import url from "../../../configs/config";
import Card from "../../../components/Card";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import TextInput from "../../../components/input/edit-input-field";
import FadeLoaderComponent from "../../../components/loader-component";
import SearchInput from "../../../components/search-input";
import PaginationComponent from "../../../components/pagination/pagination-component";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";
import { roleTypes } from "../../../data/roleType";

const colors = [
  {
    status: "active",
    color: "bg-primary",
  },
  {
    status: "in-active",
    color: "bg-warning",
  },
];

const LocationList = () => {
  const alertRef = useRef(null);
  const alertRefEdit = useRef(null);
  const [loader, setLoader] = useState(true);
  const [loading, setLoading] = useState(false);
  const [blockIsSubmit, setBlockIsSubmit] = useState(true);
  const [deleteIsSubmit, setDeleteIsSubmit] = useState(true);
  const [locations, setLocations] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedData, setSelectedData] = useState();
  const [decodedToken, setDecodedToken] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("coyote-token");
  const permissions = useSelector(SettingSelector.set_permissions);
  const apiCallInProgress = useRef(false);
  const initialLoadComplete = useRef(false);
  const abortControllerRef = useRef(null);

  const hasViewPermission = permissions?.location?.view === true;
  const hasUpdatePermission = permissions?.location?.update === true;
  const hasDeletePermission = permissions?.location?.delete === true;
  const hasCreatePermission = permissions?.location?.create === true;
  const hasStatusPermission = permissions?.location?.status === true;

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getLocations = async (offset = 0, limit = 10) => {
    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      setLoader(false);
      return;
    } else {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
        params: {
          offset,
          limit,
          search:
            searchQuery.trim().length >= 2
              ? searchQuery.trim()
              : searchQuery.trim().length < 1
                ? null
                : null,
        },
        signal: abortControllerRef.current.signal,
      };

      try {
        const response = await axios.get(`${url.backendURL}location/`, config);
        setLocations(response.data.response.data);
        setTotalEntries(response.data.response.locations_count);
        setLoader(false);
      } catch (err) {
        if (axios.isCancel(err)) {
          return;
        }

        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        handleError(err, "locations");
        setLoader(false);
      } finally {
        apiCallInProgress.current = false;
        initialLoadComplete.current = true;
      }
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  useEffect(() => {
    if (!decodedToken) return;
    if (!initialLoadComplete.current) {
      getLocations((currentPage - 1) * entriesPerPage, entriesPerPage);
    } else if (!apiCallInProgress.current) {
      getLocations((currentPage - 1) * entriesPerPage, entriesPerPage);
    }
  }, [currentPage, entriesPerPage, decodedToken]);

  useEffect(() => {
    if (!initialLoadComplete.current) return;
    const debounceTimer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2 || searchQuery.trim().length < 1) {
        setLoading(true);
        setCurrentPage(1);
        await getLocations(0, entriesPerPage);

        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleShowEdit = async (e, item) => {
    e.preventDefault();
    setSelectedData({
      name: item?.name,
      id: item?.id,
    });
    setShowEdit(true);
  };

  const handleCloseEdit = async () => {
    setShowEdit(false);
    setErrors();
  };

  const handleDeleteLocation = async (e) => {
    e.preventDefault();
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.delete(
        `${url.backendURL}location/${selectedData.id}`,
        config,
      );
      await getLocations((currentPage - 1) * entriesPerPage, entriesPerPage);

      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "locations",
      });
      setShowPopover(null);
      setDeleteIsSubmit(true);

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } catch (err) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "locations");
      setDeleteIsSubmit(true);
      setShowPopover(null);
    }
  };

  const handleEditLocation = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      if (!token) {
        localStorage.removeItem("coyote-token");

        navigate(
          `/${
            decodedToken?.roles?.some(
              (role) => role.role_type === roleTypes.SUPER_ADMIN,
            )
              ? "admin"
              : "user"
          }/auth/sign-in`,
          {
            state: { status: "invalid-token" },
          },
        );
        return;
      }
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const data = {
        name: values.name,
      };

      const response = await axios.put(
        `${url.backendURL}location/${selectedData.id}`,
        data,
        config,
      );

      await getLocations((currentPage - 1) * entriesPerPage, entriesPerPage);

      if (alertRefEdit?.current) {
        alertRefEdit.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "edit",
      });
      setTimeout(() => {
        setShowEdit(false);
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 2000);
    } catch (err) {
      if (alertRefEdit?.current) {
        alertRefEdit.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "edit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLocationStatus = async (e, status) => {
    e.preventDefault();
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }
    const locationStatus =
      status?.status === "in-active" ? "active" : "in-active";

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const data = {
        status: locationStatus,
      };
      const response = await axios.put(
        `${url.backendURL}location/${status?.id}/status`,
        data,
        config,
      );

      await getLocations((currentPage - 1) * entriesPerPage, entriesPerPage);

      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "locations",
      });
      setBlockIsSubmit(true);

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } catch (err) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "locations");

      setBlockIsSubmit(true);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (totalEntries === 0) return;
    setCurrentPage(pageNumber);
  };

  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const [showPopover, setShowPopover] = useState(null);

  const togglePopover = (idx) => {
    if (showPopover === idx) {
      setShowPopover(null);
    } else {
      setShowPopover(idx);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "Location must contain at least one letter or number",
      )
      .min(1, "Location must be at least 1 character long")
      .max(255, "Location must not exceed 255 characters")
      .trim()
      .required("Location is required"),
  });

  const getColorByStatus = (status) => {
    const colorObject = colors.find((color) => color.status === status);
    return colorObject ? colorObject.color : "";
  };

  useEffect(() => {
    function toggleStickyNames() {
      const isWideScreen = window.innerWidth > 650;

      const stickyHeadNames = document.querySelectorAll(".sticky-head-name");
      const stickyDataNames = document.querySelectorAll(".sticky-data-names");

      if (isWideScreen) {
        stickyHeadNames.forEach((head) => {
          head.classList.add("sticky-action-head-buttons");
          const parentRow = head.closest(".tr-user");
          if (parentRow) {
            head.style.height = `${parentRow.offsetHeight}px`;
          }
        });
        stickyDataNames.forEach((data) => {
          data.classList.add("sticky-action-buttons");
          const parentRow = data.closest(".tr-user");
          if (parentRow) {
            data.style.height = `${parentRow.offsetHeight}px`;
          }
        });
      } else {
        stickyHeadNames.forEach((head) => {
          head.classList.remove("sticky-action-head-buttons");
          head.style.height = "auto";
        });
        stickyDataNames.forEach((data) => {
          data.classList.remove("sticky-action-buttons");
          data.style.height = "auto";
        });
      }
    }
    toggleStickyNames();
    window.addEventListener("resize", toggleStickyNames);
    return () => window.removeEventListener("resize", toggleStickyNames);
  }, [loader, locations]);

  useEffect(() => {
    const handleScroll = () => {
      if (showPopover !== null) {
        setShowPopover(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopover]);

  return (
    <>
      <style>
        {`
          .table-user .sticky-action-buttons {
            background-color: #ffffff !important;
            z-index: 2;
          }
          .table-user .tr-user:hover .sticky-action-buttons {
            background-color: #f5f6fa !important;
          }
          .table-user .sticky-action-head-buttons {
            background: #e2e4e9 !important;
          }
          .table-user .sticky-action-head-buttons,
          .table-user .sticky-action-buttons {
            left: 0;
            z-index: 11;
            position: absolute;
            width: 260px;
            height: inherit;
            display: flex;
            align-items: center;
            padding-left: 15px;
            border-bottom: 1px solid #ebedef !important;
          }
          .table-user .sticky-action-buttons {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .table-user .th-user:nth-child(2),
          .table .td-user:nth-child(2) {
            padding-left: 275px !important;
          }
           @media (max-width: 650px) {
            .table-user .sticky-action-head-buttons,
            .table-user .sticky-action-buttons {
               position: static !important;
               width: auto !important;
               white-space: normal !important;
            }
            .table-user .th-user:nth-child(2),
            .table .td-user:nth-child(2) {
              padding-left: 0.75rem !important;
            }
          }
        `}
      </style>
      <div>
        <div className="bd-example">
          <div className="d-flex justify-content-between flex-wrap pl-0">
            <Modal
              show={showEdit}
              onHide={handleCloseEdit}
              backdrop="true"
              keyboard={true}
              dialogClassName="custom-modal-width"
            >
              <Modal.Header className="custom-modal-header">
                <Modal.Title>Edit Location</Modal.Title>
                <button
                  type="button"
                  className="btn-close-svg"
                  onClick={handleCloseEdit}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </Modal.Header>
              <Formik
                initialValues={{
                  name: selectedData?.name,
                }}
                validationSchema={validationSchema}
                onSubmit={handleEditLocation}
                enableReinitialize
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <Form className="form-horizontal p-3" onSubmit={handleSubmit}>
                    <FocusError />

                    <Modal.Body ref={alertRefEdit}>
                      {alert.alertFor === "edit" && (
                        <AlertNotificationSimple
                          type={alert.type}
                          message={alert.message}
                          show={alert.show}
                          onClose={() => setAlert({ ...alert, show: false })}
                        />
                      )}
                      <TextInput
                        label="Name"
                        name="name"
                        placeholder="Enter Name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={true}
                        errors={errors?.name}
                      />
                    </Modal.Body>

                    <Modal.Footer>
                      <Button
                        type="submit"
                        variant="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        Submit
                      </Button>
                      <Button
                        type="button"
                        variant="btn btn-danger"
                        onClick={handleCloseEdit}
                      >
                        Cancel
                      </Button>
                    </Modal.Footer>
                  </Form>
                )}
              </Formik>
            </Modal>
          </div>
        </div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between listing-card-header">
                <div className="header-title">
                  <h4 className="card-title">Location List</h4>
                </div>
              </Card.Header>
              <div className="d-flex align-items-center justify-content-between">
                <Row className="align-items-center d-flex justify-content-between mt-2 w-100">
                  {/* Show entries section */}
                  <Col
                    sm="5"
                    md="4"
                    lg="4"
                    xs="12"
                    ref={alertRef}
                    className="order-1"
                  >
                    <div className="pt-4 d-flex align-items-center col-12 ms-4 listing-entries">
                      <label className="me-2">Show</label>
                      <select
                        className="form-select py-1 ps-2 pe-0 text-sm bg-white rounded-lg w-25"
                        value={entriesPerPage}
                        onChange={handleEntriesPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <span className="ms-2">entries</span>
                    </div>
                  </Col>

                  <Col sm="7" md="8" lg="8" xs="12" className="order-2">
                    <div className="pt-4 d-flex justify-content-start align-items-center">
                      <Row className="g-2 ms-4 ms-sm-0 align-items-end w-100 justify-content-end listing-controls-row">
                        {/* Search Input */}
                        <Col
                          xs="12"
                          sm="auto"
                          md="auto"
                          lg="auto"
                          className="d-flex ps-0 me-0 me-sm-0 flex-column flex-md-row align-items-start align-items-md-center listing-search-col"
                        >
                          <label className="me-2 mb-1 mb-md-0 text-nowrap">
                            Search:
                          </label>
                          <div
                            style={{ maxWidth: "180px", width: "100%" }}
                            className="listing-search-input-wrapper pe-sm-0 pe-3"
                          >
                            <SearchInput
                              searchQuery={searchQuery}
                              setSearchQuery={setSearchQuery}
                              loading={loading}
                              setLoading={setLoading}
                            />
                          </div>
                        </Col>
                        {(hasCreatePermission ||
                          decodedToken?.roles?.some(
                            (role) => role.role_type === roleTypes.SUPER_ADMIN,
                          )) && (
                          <Col
                            xs="12"
                            sm="auto"
                            md="auto"
                            lg="auto"
                            className="d-flex justify-content-start justify-content-md-end align-items-center ps-0 ps-sm-4 listing-add-button-col"
                          >
                            <Link
                              to={`/dashboard/app/location-add`}
                              className="mt-1 mt-md-0"
                            >
                              <Button
                                type="submit"
                                variant="btn btn-primary"
                                className=" responsive-button "
                              >
                                Add Location
                              </Button>
                            </Link>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>
              <Card.Body className="px-0">
                {alert.alertFor === "locations" && (
                  <Row>
                    <Col className="mt-2 mb-2 ms-4 me-4">
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                    </Col>{" "}
                  </Row>
                )}
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                  <Table
                    id="location-list-table datatable"
                    className="table-user"
                    role="grid"
                    responsive
                    data-toggle="data-table"
                    style={{ minWidth: "1000px" }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "#e2e4e9" }}
                        className="tr-user"
                      >
                        <th className="sticky-head-name th-user">Name</th>
                        <th className="th-user">Status</th>
                        <th className="th-user">Action</th>
                      </tr>
                    </thead>
                    {!loader && locations.length > 0 ? (
                      <tbody>
                        {locations?.map((item, idx) => (
                          <tr key={idx} className="table-hover tr-user">
                            <td className="sticky-data-names td-user">
                              {item.name}
                            </td>
                            <td className="td-user">
                              {" "}
                              <span
                                className={`badge ${getColorByStatus(
                                  item.status,
                                )}`}
                              >
                                {item.status}
                              </span>
                            </td>

                            <td className="td-user">
                              <div className="flex align-items-center list-user-action">
                                {(hasStatusPermission ||
                                  decodedToken?.roles.some(
                                    (role) =>
                                      role.role_type === roleTypes.SUPER_ADMIN,
                                  )) && (
                                  <>
                                    {" "}
                                    <Link
                                      className={`btn btn-sm btn-icon ${
                                        item?.status === "in-active"
                                          ? "btn-success"
                                          : "btn-danger"
                                      } `}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      onClick={(e) => {
                                        if (blockIsSubmit) {
                                          setBlockIsSubmit(false);
                                          handleLocationStatus(e, item);
                                        }
                                      }}
                                      title={
                                        item?.status === "in-active"
                                          ? "Active"
                                          : "In-active"
                                      }
                                      data-original-title={
                                        item?.status === "in-active"
                                          ? "Active"
                                          : "In-active"
                                      }
                                    >
                                      <span className="btn-inner">
                                        {item?.status === "in-active" ? (
                                          <CheckCircleOutlineIcon
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                            }}
                                          />
                                        ) : (
                                          <FiMinusCircle
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                            }}
                                          />
                                        )}
                                      </span>
                                    </Link>{" "}
                                  </>
                                )}
                                {(hasUpdatePermission ||
                                  decodedToken?.roles.some(
                                    (role) =>
                                      role.role_type === roleTypes.SUPER_ADMIN,
                                  )) && (
                                  <>
                                    {" "}
                                    <Link
                                      className={`btn btn-sm btn-icon btn-warning `}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title="Edit"
                                      data-original-title="Edit"
                                      onClick={(e) => {
                                        setShowPopover(null);
                                        handleShowEdit(e, item);
                                      }}
                                    >
                                      <span className="btn-inner">
                                        <svg
                                          width="20"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            d="M15.1655 4.60254L19.7315 9.16854"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                        </svg>
                                      </span>
                                    </Link>{" "}
                                  </>
                                )}
                                {(hasDeletePermission ||
                                  decodedToken?.roles.some(
                                    (role) =>
                                      role.role_type === roleTypes.SUPER_ADMIN,
                                  )) && (
                                  <OverlayTrigger
                                    trigger="click"
                                    show={showPopover === `delete-${idx}`}
                                    onToggle={() =>
                                      togglePopover(`delete-${idx}`)
                                    }
                                    placement="top"
                                    overlay={
                                      <Popover id={`popover-delete-${idx}`}>
                                        <Popover.Header as="h2">
                                          <strong>Delete</strong>{" "}
                                          <span>{`(${item.name})`}</span> ?
                                        </Popover.Header>
                                        <Popover.Body>
                                          Once deleted, this action cannot be
                                          undone.
                                          <div className="d-flex justify-content-end mt-2">
                                            <Button
                                              size="sm"
                                              variant="danger"
                                              className="me-2"
                                              onClick={(e) => {
                                                if (deleteIsSubmit) {
                                                  setDeleteIsSubmit(false);
                                                  handleDeleteLocation(e);
                                                }
                                              }}
                                            >
                                              Delete
                                            </Button>
                                            <Button
                                              size="sm"
                                              className="text-black bg-white"
                                              variant="secondary"
                                              onClick={() =>
                                                togglePopover(`delete-${idx}`)
                                              }
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </Popover.Body>
                                      </Popover>
                                    }
                                  >
                                    <Link
                                      className={`btn btn-sm btn-icon btn-danger `}
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title="Delete"
                                      data-original-title="Delete"
                                      onClick={(e) =>
                                        setSelectedData({
                                          name: item?.name,
                                          id: item?.id,
                                        })
                                      }
                                    >
                                      <span className="btn-inner">
                                        <svg
                                          width="20"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          stroke="currentColor"
                                        >
                                          <path
                                            d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            d="M20.708 6.23975H3.75"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                        </svg>
                                      </span>
                                    </Link>
                                  </OverlayTrigger>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ) : loader ? (
                      <tbody>
                        <tr>
                          <td colSpan={3} className="td-user">
                            <FadeLoaderComponent />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody>
                        <tr>
                          <td colSpan={3} className="td-user">
                            <div className="d-flex w-100 justify-content-center">
                              <p>No location found</p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </Table>
                </div>
                <Row className="align-items-center mt-2">
                  <Col sm="6">
                    <div className="d-flex flex-wrap items-center ms-4">
                      <div className="">
                        Showing{" "}
                        {Math.min(
                          (currentPage - 1) * entriesPerPage + 1,
                          totalEntries,
                        )}{" "}
                        to{" "}
                        {Math.min(currentPage * entriesPerPage, totalEntries)}{" "}
                        of {totalEntries} entries
                      </div>
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="bd-example d-flex justify-content-center justify-content-md-end me-4">
                      <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default LocationList;
